import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { supabase } from "../lib/supabase";
import ListingCard from "../components/ListingCard";

export default function Favorites() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [favoriteListings, setFavoriteListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadFavoriteListings = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      setLoading(true);
      setError("");
      
      try {
        console.log('Loading favorites for user:', user.id);
        
        // Get favorite listings with all necessary data
        const { data: favorites, error: favoritesError } = await supabase
          .from('favorites')
          .select(`
            id,
            created_at,
            listings!inner (
              id,
              title,
              price_cents,
              category,
              age_range,
              condition,
              description,
              location,
              latitude,
              longitude,
              status,
              tags,
              created_at,
              updated_at,
              photos,
              user_id
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        console.log('Favorites query result:', { favorites, favoritesError });
        
        if (favoritesError) {
          console.error('Favorites query error:', favoritesError);
          throw favoritesError;
        }

        // Get user profiles for the listings
        const listingIds = favorites.map(fav => fav.listings.user_id);
        console.log('Listing IDs for profiles:', listingIds);
        
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name, avatar_url')
          .in('id', listingIds);

        console.log('Profiles query result:', { profiles, profilesError });
        
        if (profilesError) {
          console.error('Profiles query error:', profilesError);
          throw profilesError;
        }

        // Create a profile lookup map
        const profileMap = {};
        profiles.forEach(profile => {
          profileMap[profile.id] = profile;
        });

        // Transform the data to match the expected format
        const listings = favorites.map(fav => ({
          ...fav.listings,
          user: profileMap[fav.listings.user_id] || { id: fav.listings.user_id, name: 'Unknown User' },
          favorite_id: fav.id,
          favorited_at: fav.created_at
        }));

        console.log('Final listings data:', listings);
        setFavoriteListings(listings);
      } catch (err) {
        console.error('Failed to load favorite listings - Full error:', err);
        setError(`Failed to load your favorite listings: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    loadFavoriteListings();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="container-center mx-auto">
        <div className="mb-8 text-center">
          <div className="h-8 bg-slate-200 rounded-lg loading-skeleton mb-2"></div>
          <div className="h-4 bg-slate-200 rounded-lg loading-skeleton w-2/3 mx-auto"></div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="h-40 bg-slate-200 loading-skeleton"></div>
              <div className="p-3 space-y-3">
                <div className="h-4 bg-slate-200 rounded loading-skeleton"></div>
                <div className="h-3 bg-slate-200 rounded loading-skeleton w-2/3"></div>
                <div className="h-6 bg-slate-200 rounded loading-skeleton w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-center mx-auto">
      {/* Page Header */}
      <div className="mb-8 text-center">
        <h1 className="text-responsive-xl font-bold text-slate-900 mb-2">
          {t('page.favorites')}
        </h1>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-6">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs">!</span>
            </div>
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      {/* Favorites Grid */}
      {favoriteListings.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
            <span className="text-4xl">❤️</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No favorites yet</h3>
          <p className="text-slate-600 mb-6">Start exploring and heart the items you love!</p>
          <button 
            className="btn-primary"
            onClick={() => navigate('/browse')}
          >
            Browse Items
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {favoriteListings.map((listing) => (
            <ListingCard key={listing.id} item={listing} />
          ))}
        </div>
      )}
    </div>
  );
}
